import {
  type ActionKey,
  baseFreeFormatTypes,
  type DuplicateScopeType,
  type ElementValidation,
  type ElementValidationData,
  type ElementValidationPanelData,
  type GenericObject,
  type ReuseDialogConfig,
  type ToolbarActionInitConfig,
  type ValidationStatus,
  type ValidationStatusConfig,
  type ValidationStatusDescriptionMap,
  ValidationStatusType,
  type ValidationStatusTypeKey,
} from '@bizzdesign/modeler/models';
import { getElementDefaultName } from '@bizzdesign/modeler/utils-modeler';
import { getElementIcon } from '../data/selected-element.utils';

export const descriptionMap: ValidationStatusDescriptionMap = {
  NEW_ELEMENT: 'New element',
  DUPLICATE: 'Duplicate element in repository',
  NOT_PROMOTED: 'Shape will not be published to repository',
  INVALID_PARENT_CHILD: 'Invalid nesting combination',
  INVALID_LINK_TYPE: 'Relation type or relation target/source not valid',
  INVALID_TARGET_TYPE: 'Relation type or relation target/source not valid',
  INVALID_SOURCE_TYPE: 'Relation type or relation target/source not valid',
  INVALID_TYPE: 'No or invalid type for element',
  NO_NAME: 'Element has no name',
  DELETED_ELEMENT: 'Element deleted from source Repository',
};

export const generateBaseConfigObject = (
  keys: ActionKey[],
): ToolbarActionInitConfig[] =>
  keys.map<ToolbarActionInitConfig>((key) => ({
    key,
    shouldTriggerManualSave: false,
  }));

//#region Validation specific
export const getValidationPanelData = (
  data: ElementValidationData[],
  fallbackSelectGraphicFn: (message: string) => void,
  showDialogFn: ((reuseDialogConfig: ReuseDialogConfig) => void) | undefined,
  selectGraphicFn: (id: string) => void,
): ElementValidationPanelData[] => {
  const resultData: ElementValidationPanelData[] = [];
  for (const elementData of data) {
    const element = elementData.element;
    const promotion = element['promotion'];
    const isDeletedObject = element['metadata']?.isDeletedFromRepo;

    if (promotion?.semanticsId && !isDeletedObject) {
      continue;
    }

    const typeInfo = promotion ? promotion.typeName : element['typeName'];
    const defaultName = getElementDefaultName(element);
    const icon = getElementIcon(element);
    const isGraphicLockedByPeer = elementData.element['isGraphicLockedByPeer'];

    const elementValidationData: ElementValidationPanelData = {
      id: element['id'],
      name: (element['name']?.en || defaultName) ?? '',
      type: typeInfo,
      icon: icon,
      status: getValidationStatus(elementData),
      isGraphicLockedByPeer,
      statusConfig: getValidationStatusConfig(
        elementData,
        fallbackSelectGraphicFn,
        showDialogFn,
        selectGraphicFn,
      ),
    };
    resultData.push(elementValidationData);
  }
  return resultData;
};

export const getValidationStatus = (
  elementData: ElementValidationData,
): ValidationStatus => {
  const hasWarning = !!elementData.warningType;
  const hasError = !!elementData.errorType;
  const { element } = elementData;

  if (!hasError && !hasWarning) return 'success';
  const isBasicElement = baseFreeFormatTypes.includes(element['typeName']);

  if (elementData.errorType === 'NO_NAME' && isBasicElement) {
    return 'warning';
  }

  // error has greater priority
  if (hasError) return 'error';

  return 'warning';
};

export const getValidationStatusConfig = (
  elementData: ElementValidationData,
  fallbackSelectGraphicFn: (message: string) => void,
  showDialogFn: ((reuseDialogConfig: ReuseDialogConfig) => void) | undefined,
  selectGraphicFn: (id: string) => void,
): ValidationStatusConfig => {
  let description: string = descriptionMap.NEW_ELEMENT;
  let type: ValidationStatusType = ValidationStatusType.NEW_ELEMENT;
  const { warningType, errorType, element } = elementData;
  if (!!warningType) {
    const warningTypeKey = warningType as ValidationStatusTypeKey;
    description = descriptionMap[warningTypeKey] ?? '';
    type = ValidationStatusType[warningTypeKey];
  }

  if (!!errorType) {
    const errorTypeKey = errorType as ValidationStatusTypeKey;
    description = descriptionMap[errorTypeKey] ?? '';
    type = ValidationStatusType[errorTypeKey];
  }
  const isBasicElement = baseFreeFormatTypes.includes(element['typeName']);
  if (errorType === 'NO_NAME' && isBasicElement) {
    const warningTypeKey = 'NOT_PROMOTED';
    description = descriptionMap[warningTypeKey] ?? '';
    type = ValidationStatusType[warningTypeKey];
  }

  const reuseElementDialogConfig = getElementReuseDialogConfig(elementData);
  const isDeletedElement = errorType === 'DELETED_ELEMENT';
  const shouldShowReuseAction = showDialogFn && !isDeletedElement;

  return {
    description,
    type,
    duplicateScope: reuseElementDialogConfig.metadata
      ?.location as DuplicateScopeType,
    ...(shouldShowReuseAction && {
      actionCallback: () => showDialogFn(reuseElementDialogConfig),
    }),
    selectGraphic: () =>
      elementData.element['id']
        ? selectGraphicFn(elementData.element['id'])
        : fallbackSelectGraphicFn(
            'the selectGraphic function or the element ID is not defined.',
          ),
  };
};

export const getElementReuseDialogConfig = (
  elementData: ElementValidationData,
): ReuseDialogConfig => {
  const { element, acceptBtnFn } = elementData;
  const formatTypeName = (typeName: string) =>
    typeName ? typeName.split(/(?=[A-Z][a-z])/).join(' ') : '';

  return {
    metadata: {
      name: element['name'] ? element['name'].en : '',
      type: element['promotion']
        ? formatTypeName(element['promotion'].typeName)
        : formatTypeName(element['typeName']),
      location: elementData.element['notUniqueScope'],
      elementId: element['id'],
    },
    acceptBtnFn: acceptBtnFn,
  };
};

const getDuplicateScopeTooltip = (
  item: ElementValidationPanelData,
): string | undefined => {
  const statusType = item.statusConfig.type;

  if (
    statusType === ValidationStatusType.DUPLICATE &&
    item.statusConfig.duplicateScope
  ) {
    return item.statusConfig.duplicateScope === 'repository'
      ? 'modelingAssistance.replaceElementTooltip'
      : 'modelingAssistance.replaceDraftTooltip';
  }

  if (statusType === ValidationStatusType.DELETED_ELEMENT) {
    return 'modelingAssistance.deletedElementTooltip';
  }

  return undefined;
};

export const toElementValidation = (
  panelData: ElementValidationPanelData[],
): ElementValidation[] => {
  return panelData.map((item) => {
    const isItemDuplicate =
      item.statusConfig.type === ValidationStatusType.DUPLICATE;

    return {
      id: item.id,
      name: item.name,
      description: item.statusConfig.description,
      type: item.type,
      icon: item.icon,
      status: item.status,
      statusType: item.statusConfig.type,
      isGraphicLockedByPeer: item.isGraphicLockedByPeer,
      duplicateScopeTooltip: getDuplicateScopeTooltip(item),
      actionCallback: isItemDuplicate
        ? item.statusConfig.actionCallback
        : undefined,
      selectGraphic: item.statusConfig.selectGraphic,
    } as ElementValidation;
  });
};

export const toElementValidationData = (
  data: GenericObject[],
): ElementValidationData[] =>
  data
    .filter((diagram) => diagram['elementKind'] === 'diagram')
    .map((diagram) => {
      const matchingModel = data.find(
        (item) =>
          item['elementKind'] === 'model' &&
          item['id'] === diagram['semanticsId'],
      );
      const mergedResult = matchingModel
        ? {
            ...diagram,
            ...Object.fromEntries(
              Object.entries(matchingModel).filter(
                ([key]) => diagram[key] === undefined,
              ),
            ),
          }
        : diagram;
      return { element: mergedResult };
    });
//#endregion
